import { addRule } from 'redux-ruleset'
import config from 'config'
import { LOCATION_CHANGED } from 'modules/navigation/const'
import { PURCHASE } from 'theme/templates/checkout/events'
import { getAccountContext } from 'hooks/account/useAccount/useAccount'
import getPageInfo from 'utils/getPageInfo'
import { LISTING_CONTEXT_BRAND_NAME } from 'theme/templates/Category/events'
import { FETCH_SUCCESS as PDP_FETCH_SUCCESS } from 'modules/productDetail/const'
import { getDisplayVariant } from 'modules/productDetail/selectors'

/**
 * Emarsys Web Extend
 * setEmail & cart event should be pushed on each location change
 * the purchase event should be pushed with an empty cart before
 * on the purchase event the shipping, tax and voucher cost should be added to the first article position
 * the view (pdp) and category event only if visited pdp or category
 * go should be pushed AFTER all other events are pushed
 *
 * on each cart changes the cart event + go should be pushed, debounce rate is 0.5 sec to prevent multiple events for every amount change
 *
 * the cart event should be pushed brutto prices or netto prices depending on the channel
 *
 * if devMode is active, the testMode should be pushed (prevents testing data)
 */

declare global {
  interface Window {
    ScarabQueue: any
  }
}

const createReadyContext = (onReady: () => void) => {
  let running = 0
  return () => {
    running++
    return () => {
      running--
      if (running === 0) onReady()
    }
  }
}

// the go event to push all events in queue
const ScarabQueueGo = () => {
  if (config.features.devMode) window.ScarabQueue.push(['testMode'])
  window.ScarabQueue.push(['go'])
}

/**
 * ScarabPush for every location change
 * push cart and email on each location change if available, or wait for the initial account context
 * push category or pdp view if visited and wait for the data
 * push go after all events are pushed
 */
addRule({
  id: 'emarsysWebExtend/LOCATION_CHANGED',
  target: LOCATION_CHANGED,
  output: '#emarsys-webextend-event',
  condition: (action) => {
    const path = action.payload.pathname
    const prevPath = action.meta.prevLocation?.pathname
    let isUserPath = path.includes('/checkout/')
    // ignore all checkout pages except if prevPath is login for correct merged cart
    if (prevPath === '/checkout/login/') isUserPath = false
    if (path.includes('/account/')) isUserPath = true

    if (path.includes('/account/') && prevPath === '/account/login/')
      isUserPath = false
    if (path === '/checkout/register/' || path === '/checkout/guest/')
      isUserPath = true

    return (
      !!config.features.emarsysWebExtend && window.ScarabQueue && !isUserPath
    )
  },
  consequence: (action, { addRule }) => {
    const accountContext = getAccountContext()
    const path = action.payload.pathname
    const pageInfo = getPageInfo(path)
    const createRunner = createReadyContext(() => ScarabQueueGo())
    let subRuleTriggered = false

    // if user login, wait for "SET_INITIAL_CART" action to get correct cart
    if (
      (action.meta.prevLocation?.pathname === '/account/login/' &&
        path === '/account/') ||
      action.meta.prevLocation?.pathname === '/checkout/login/'
    ) {
      const ready = createRunner()
      subRuleTriggered = true
      addRule('waitForInitialAccountContext', { ready })
    }

    // check if accountContext is existing, if not wait for "SET_INITIAL_CART" action (initial visit has no accountContext after location_change event)
    else if (accountContext?.context) {
      if (accountContext?.context?.email)
        window.ScarabQueue.push(['setEmail', accountContext.context.email])
      if (accountContext?.cart?.data.items)
        window.ScarabQueue.push([
          'cart',
          accountContext.cart.data.items.map((item) => ({
            item: item.sku,
            price:
              accountContext.channel === 'b2c'
                ? item.prices.packPriceNet
                : item.prices.packPriceGross,
            quantity: item.quantity
          }))
        ])
    } else {
      const ready = createRunner()
      subRuleTriggered = true
      addRule('waitForInitialAccountContext', { ready })
    }

    if (pageInfo.pageType === 'Category') {
      const ready = createRunner()
      subRuleTriggered = true
      addRule('waitForCategory', { ready })
    } else if (pageInfo.pageType === 'Product') {
      const ready = createRunner()
      subRuleTriggered = true
      addRule('waitForPDP', { ready })
    }

    if (!subRuleTriggered) ScarabQueueGo()
  },
  subRules: {
    waitForCategory: {
      target: LISTING_CONTEXT_BRAND_NAME,
      output: '#emarsys-webextend-event',
      debounce: 50,
      addOnce: true,
      consequence: (_, { getState, context }) => {
        const state = getState()
        window.ScarabQueue.push([
          'category',
          state.listing.category.filterValues.category
        ])
        context.get('ready')()
      }
    },
    waitForPDP: {
      target: PDP_FETCH_SUCCESS,
      output: '#emarsys-webextend-event',
      debounce: 50,
      addOnce: true,
      consequence: (_, { getState, context }) => {
        const state = getState()
        const product = getDisplayVariant(state.productDetail)
        window.ScarabQueue.push(['view', product.sku])
        context.get('ready')()
      }
    },
    waitForInitialAccountContext: {
      target: 'SET_INITIAL_CART',
      output: '#emarsys-webextend-event',
      debounce: 50,
      addOnce: true,
      consequence: (_, { context }) => {
        const accountContext = getAccountContext()
        if (accountContext?.context?.email)
          window.ScarabQueue.push(['setEmail', accountContext.context.email])
        if (accountContext?.cart?.data.items)
          window.ScarabQueue.push([
            'cart',
            accountContext.cart.data.items.map((item) => ({
              item: item.sku,
              price:
                accountContext.channel === 'b2c'
                  ? item.prices.packPriceNet
                  : item.prices.packPriceGross,
              quantity: item.quantity
            }))
          ])
        context.get('ready')()
      }
    }
  }
})

/**
 * ScarabPush for order completion
 * https://dev.emarsys.com/docs/web-extend-reference/23905885d31e4-purchase-web-extend-command
 * tax, voucher and shipping cost added to the first cart position
 */
addRule({
  id: 'emarsysWebExtend/PURCHASE',
  target: PURCHASE,
  output: '#emarsys-webextend-event',
  condition: (_) => {
    return !!config.features.emarsysWebExtend && window.ScarabQueue
  },
  consequence: (action) => {
    const accountContext = getAccountContext()
    if (accountContext?.context?.email)
      window.ScarabQueue.push(['setEmail', accountContext.context.email])
    window.ScarabQueue.push(['cart', []])

    const purchaseItems = action.payload.items.map((item) => ({
      item: item.product?.sku,
      price: item.totalPrice,
      quantity: item.amount
    }))
    // add shipping, tax and voucher cost to first item
    purchaseItems[0].price += action.meta.order.deliveryCost

    window.ScarabQueue.push([
      'purchase',
      {
        orderId: action.meta.order.id,
        items: purchaseItems
      }
    ])
    ScarabQueueGo()
  }
})

/**
 * ScarabPush Cart event for every cart action
 * https://dev.emarsys.com/docs/web-extend-reference/469506ac08e6a-cart-web-extend-command
 *
 * debounce of 0.5s to prevent multiple events for every amount change
 */
addRule({
  id: 'emarsysWebExtend/UPDATE_CART',
  target: ['CART_ADD_ITEM', 'CART_UPDATE_ITEM', 'CART_REMOVE_ITEM'],
  output: '#emarsys-webextend-event',
  concurrency: 'LAST',
  debounce: 500,
  condition: (_) => {
    return !!config.features.emarsysWebExtend && window.ScarabQueue
  },
  consequence: (action) => {
    const accountContext = getAccountContext()

    const cartItems = action.cart.items.map((item) => ({
      item: item.sku,
      price:
        accountContext?.channel === 'b2c'
          ? item.prices.packPriceNet
          : item.prices.packPriceGross,
      quantity: item.quantity
    }))

    window.ScarabQueue.push(['cart', cartItems])
    ScarabQueueGo()
  }
})
